import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootStore } from "../../state/store";
import { Row } from "../../components/Row";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { Ruling } from "../../components/Ruling";
import { DoubleClickable } from "../../components/DoubleClickable";
import { blacklistedWords } from "../../util/DANGERblacklistedWords";
import { swipeCutoff } from "../../util/swiping";
import { Swiping } from "../../types/generalTypes";
import { getTimeDisplay } from "../../util/timeUtil";
import QRGenerator from "qrcode-generator";
import jsQR from "jsqr";
import { useRequestHandler } from "../../hooks/requestHandler";
import { VerseType } from "../../types/practiceTypes";

const BEFOREHAND = "BEFOREHAND";
const READY = "READY";
const ANSWERING = "ANSWERING";
const RULING = "RULING";
const verseCardsStateKey = "verse-cards-state";
type VerseCardsPhaseType =
  | typeof BEFOREHAND
  | typeof READY
  | typeof ANSWERING
  | typeof RULING
  | "DONE";
interface WordType {
  word: string;
  status: "" | "C" | "I";
}

interface VerseCardsGameProps {
  material: VerseType[];
  allowImport?: boolean;
  maxTime?: number;
  onComplete?: () => void;
}

export function VerseCardsGame(props: VerseCardsGameProps) {
  const handler = useRequestHandler({});
  const navigate = useNavigate();
  const { transcript, resetTranscript } = useSpeechRecognition();
  const material = useSelector((state: RootStore) => state.material.material);
  const settings = useSelector((state: RootStore) => state.settings);
  const chapters = settings.selectedChapters;

  const [randomized, setRandomized] = useState(props.material);
  const [words, setWords] = useState<WordType[]>([]);
  const displayWords = useMemo(() => words.filter((w) => !!w.status), [words]);
  const [phase, setPhase] = useState<VerseCardsPhaseType>(BEFOREHAND);
  const phaseRef = useRef<VerseCardsPhaseType>(BEFOREHAND);

  const [showQRExport, setShowQRExport] = useState(false);
  const [showQRImport, setShowQRImport] = useState(false);
  const videoStream = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (phase === "BEFOREHAND" && props.material.length > 0) {
      setPhase("READY");
      setRandomized(props.material);
      stats.current.totalVerses = props.material.length;
      setWords(
        props.material[0].verse.split(" ").map((w) => ({ word: w, status: "" }))
      );
    }
  }, [props.material]);

  const importFromQR = (code: string, displayError = false) => {
    try {
      const data = JSON.parse(code);
      stats.current = data.s;
      const newRandomized: VerseType[] = [];
      data.v.forEach((item: string) => {
        // book-chapter-verse
        const parts = item.split("-");
        const book = Number.parseInt(parts[0]);
        const chapter = Number.parseInt(parts[1]);
        const verseNum = Number.parseInt(parts[2]);
        const verse = material[book].chapters[chapter][verseNum];
        newRandomized.push({
          book,
          chapter,
          verseNum,
          reference: `${material[book].bookName} ${chapter + 1}:${
            verseNum + 1
          }`,
          verse: verse.verse,
          questions: verse.questions,
        });
      });

      // Set state variables and finish up
      setRandomized(newRandomized);
      setPhase("READY");
      setShowQRImport(false);
      videoStream.current?.getTracks()?.[0]?.stop();
      return true;
    } catch {
      // Data invalid for any reason
      if (displayError) {
        handler.showMessage("The QR Code is an invalid format.", "error");
      }
      return false;
    }
  };

  useEffect(() => {
    if (!showQRImport) return;
    // Get video and canvas elements
    const video = document.createElement("video");
    const canvasElement = document.getElementById(
      "canvas"
    ) as HTMLCanvasElement | null;
    if (!video || !canvasElement) return;
    const canvas = canvasElement.getContext("2d");
    if (!canvas) return;
    if (!navigator.mediaDevices) {
      handler.showMessage(
        "QR scanning is not available on this device, but you can upload a screenshot of a QR code.",
        "error"
      );
      return;
    }
    navigator.mediaDevices.getUserMedia({ video }).then((stream) => {
      // Put the stream on the video element
      video.srcObject = stream;
      videoStream.current = stream;
      // @ts-ignore
      video.setAttribute("playsinline", true); // required to tell iOS safari we don't want fullscreen
      video.play();
      let interval: NodeJS.Timer | null = null;

      const tick = () => {
        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          // Paint the canvas with the image from the stream
          canvasElement.hidden = false;
          canvasElement.height = video.videoHeight;
          canvasElement.width = video.videoWidth;
          canvas.drawImage(
            video,
            0,
            0,
            canvasElement.width,
            canvasElement.height
          );

          // Get the image
          const imageData = canvas.getImageData(
            0,
            0,
            canvasElement.width,
            canvasElement.height
          );
          // Attempt to decode the qr
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: "dontInvert",
          });
          if (code) {
            if (importFromQR(code.data)) {
              // Import successful
              handler.showMessage("Verses imported!", "success");
              if (interval) clearInterval(interval);
            }
          }
        }
      };

      // Check for a qr code every 50 ms
      interval = setInterval(() => {
        requestAnimationFrame(tick);
      }, 50);
    });
  }, [showQRImport]);

  useEffect(() => {
    if (showQRExport) {
      try {
        // Assemble data to put into the qr
        const content = {
          s: stats.current, // stats
          v: [] as string[], // remaining verses
        };
        randomized.forEach((r) => {
          content.v.push(`${r.book}-${r.chapter}-${r.verseNum}`);
        });
        const qr = QRGenerator(0, "H"); // high tolerance for error correction
        qr.addData(JSON.stringify(content));
        qr.make();
        // Add resulting image to the div
        const el = document.getElementById("qr-code");
        if (el) {
          el.innerHTML = qr.createImgTag().replace(/210/g, "350");
        }
      } catch {
        // Any error, but probably too much data
        handler.showMessage(
          "There are too many verses remaining to export a QR code.",
          "error"
        );
      }
    }
  }, [showQRExport]);

  // Most probable ruling, which could be nothing
  const prioritizeRuling = useMemo(() => {
    let ruling = 0 as number | undefined;
    if (phase !== "RULING") return ruling;
    words.forEach((w) => {
      if (w.status === "I") ruling = 1;
      if (w.status === "" && ruling === 0) ruling = undefined;
    });
    return ruling;
  }, [words, phase]);

  const swipeStatusDefault: Swiping = {
    sX: -1,
    sY: -1,
    eX: -1,
    eY: -1,
  };
  const [swipeStatus, setSwipeStatus] = useState<Swiping>(swipeStatusDefault);

  const stats = useRef<{
    totalVerses: number;
    correct: number;
    incorrect: number;
    time: number;
  }>({
    totalVerses: 0,
    correct: 0,
    incorrect: 0,
    time: 0,
  });
  const [startTimekeeper, setStartTimekeeper] = useState(0);
  const timekeeperId = useRef<any>(null);
  const timeTaken = useRef<number>(0);
  useEffect(() => {
    if (startTimekeeper) {
      const newId = setInterval(() => {
        timeTaken.current = timeTaken.current + 0.2;
      }, 200);
      timekeeperId.current = newId;
    }
    return () => clearTimeout(timekeeperId.current);
  }, [startTimekeeper]);

  // Verse with some words possibly in red
  const getStyledVerse = (verse: string, compare: string) => {
    if (!compare) return verse;
    const words = verse.split(" ");
    const compareWords = compare.split(" ");
    return (
      <span>
        {words.map((word, index) => {
          blacklistedWords.forEach((blacklisted) => {
            if (word.includes(blacklisted)) {
              const censored =
                blacklisted.substring(0, 1) +
                "*".repeat(blacklisted.length - 1);
              word = word.replace(blacklisted, censored);
            }
          });

          let included = false;
          compareWords.forEach((word2) => {
            if (
              word.replace(/[^a-zA-Z0-9]+/g, "").toLowerCase() ===
              word2.replace(/[^a-zA-Z0-9]+/g, "").toLowerCase()
            )
              included = true;
          });
          return (
            <span key={index} style={{ color: included ? undefined : "red" }}>
              {word + " "}
            </span>
          );
        })}
      </span>
    );
  };

  const clickNextVerse = () => {
    setPhase(ANSWERING);
    setShowQRExport(false);
    phaseRef.current = ANSWERING;
    setStartTimekeeper(Math.random());
    timeTaken.current = 0;
    resetTranscript();
    if (settings.vcVoiceToText)
      SpeechRecognition.startListening({ continuous: true });
  };
  const clickShowAnswer = () => {
    setPhase(RULING);
    phaseRef.current = RULING;
    window.scrollTo(0, 0);
    clearTimeout(timekeeperId.current);
    SpeechRecognition.stopListening();
  };
  const makeRuling = (theRuling?: number) => {
    setRandomized((oldValue) => {
      const newRandomized = [...oldValue];
      setWords((words) => {
        const ruling =
          theRuling !== undefined
            ? theRuling
            : words.some((w) => w.status === "I")
            ? 1
            : 0;
        if (ruling === 2 || (ruling === 1 && settings.vcReview)) {
          newRandomized.push(newRandomized[0]);
        }
        if (ruling === 0) stats.current.correct = stats.current.correct + 1;
        if (ruling === 1) stats.current.incorrect = stats.current.incorrect + 1;
        if (ruling !== 2) {
          stats.current.time = stats.current.time + timeTaken.current;
        }
        newRandomized.splice(0, 1);

        const newPhase = newRandomized.length > 0 ? READY : "DONE";
        setPhase(newPhase);

        phaseRef.current = newPhase;

        const currentState = JSON.stringify({
          versesRemaining: newRandomized,
          stats: stats.current,
        });
        localStorage.setItem(verseCardsStateKey, currentState);

        if (newRandomized.length > 0) {
          return newRandomized[0].verse
            .split(" ")
            .map((w) => ({ word: w, status: "" }));
        } else {
          return [];
        }
      });
      return newRandomized;
    });
  };
  const upHandler = (e: KeyboardEvent) => {
    if (e.code === "Space") {
      switch (phaseRef.current) {
        case READY:
          clickNextVerse();
          break;
        case ANSWERING:
          clickShowAnswer();
          break;
        case RULING:
          makeRuling();
          break;
        default:
          break;
      }
      return;
    }
    if (phaseRef.current === "ANSWERING") {
      const keyPressed = e.code.substring(3, 4).toLowerCase();
      if (keyPressed.match(/[a-z0-9]/i)) {
        setWords((words) => {
          const newWords = [...words];
          const index = newWords.findIndex((w) => w.status === "");
          const firstLetter = newWords[index].word
            .toLowerCase()
            .match(/[a-z0-9]/)
            ?.pop();
          newWords[index] = {
            ...newWords[index],
            status: firstLetter && firstLetter === keyPressed ? "C" : "I",
          };
          if (index + 1 === newWords.length) clickShowAnswer();
          return newWords;
        });
      }
    }
  };
  useEffect(() => {
    window.addEventListener("keyup", upHandler);

    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener("keyup", upHandler);
      SpeechRecognition.stopListening();
    };
  }, []);

  if (chapters.length === 0) {
    return <Navigate to="/" />;
  }

  return (
    <DoubleClickable
      className="page"
      style={{
        padding: 20,
        touchAction: phase === RULING ? "none" : "auto",
        minHeight: "calc(100vh - 20px)",
        minWidth: 300,
      }}
      doubleClick={() => {
        if (settings.touchscreen) {
          if (phase === READY && randomized.length > 0) {
            clickNextVerse();
          }
          if (phase === ANSWERING) {
            clickShowAnswer();
          }
        }
      }}
      onTouchStart={(e) => {
        setSwipeStatus({
          ...swipeStatus,
          sX: e.touches[0].clientX,
          eX: e.touches[0].clientX,
          sY: e.touches[0].clientY,
          eY: e.touches[0].clientY,
        });
      }}
      onTouchMove={(e) => {
        setSwipeStatus({
          ...swipeStatus,
          eX: e.touches[0].clientX,
          eY: e.touches[0].clientY,
        });
      }}
      onTouchEnd={() => {
        if (settings.touchscreen && phase === RULING) {
          if (Math.abs(swipeStatus.eX - swipeStatus.sX) > swipeCutoff) {
            makeRuling(swipeStatus.eX > swipeStatus.sX ? 0 : 1);
          }
        }
        setSwipeStatus(swipeStatusDefault);
      }}
    >
      {phase === RULING && settings.touchscreen && swipeStatus.sX >= 0 && (
        <div
          style={{
            backgroundColor: swipeStatus.sX < swipeStatus.eX ? "green" : "red",
            height: 5,
            position: "absolute",
            left: Math.min(swipeStatus.sX, swipeStatus.eX),
            top: swipeStatus.sY - 5,
            width: Math.abs(swipeStatus.sX - swipeStatus.eX),
          }}
        />
      )}

      <div style={{ height: 250, marginTop: 10 }}>
        {phase === READY &&
          stats.current.correct === 0 &&
          stats.current.incorrect === 0 && (
            <>
              {localStorage.getItem(verseCardsStateKey) && (
                <button
                  className="link"
                  onClick={() => {
                    const previousState = JSON.parse(
                      localStorage.getItem(verseCardsStateKey) as string
                    );
                    setRandomized(previousState.versesRemaining);
                    stats.current = previousState.stats;
                  }}
                >
                  Restore Previous State
                </button>
              )}
              <br />
              <br />
              {!showQRImport && (
                <button className="link" onClick={() => setShowQRImport(true)}>
                  QR Code Import
                </button>
              )}
              {showQRImport && (
                <>
                  <p>
                    You can upload a screenshot of a QR code by clicking the
                    button below.
                  </p>
                  <label
                    htmlFor="qr-upload"
                    className="back-button"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Upload QR Code Picture
                  </label>
                  <input
                    type="file"
                    id="qr-upload"
                    className="back-button"
                    accept="image/png, image/jpeg"
                    style={{ display: "none" }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];

                      if (!file) return;
                      const url = URL.createObjectURL(file);

                      const img = document.createElement("img");
                      img.src = url;
                      document.body.appendChild(img);
                      // @ts-ignore
                      img.addEventListener("load", () => {
                        const canvas = document.createElement("canvas"); // Creates a canvas  object
                        canvas.width = img.naturalWidth; // Assigns image's width to canvas
                        canvas.height = img.naturalHeight; // Assigns image's height to canvas

                        const context = canvas.getContext("2d"); // Creates a contect object
                        context!.imageSmoothingEnabled = false;
                        context!.drawImage(
                          img,
                          0,
                          0,
                          img.naturalWidth,
                          img.naturalHeight
                        ); // Draws the image on canvas

                        const imageData = context!.getImageData(
                          0,
                          0,
                          img.naturalWidth,
                          img.naturalHeight
                        ); // Assigns image base64 string in jpeg format to a variable
                        const code = jsQR(
                          imageData.data,
                          img.naturalWidth,
                          img.naturalHeight
                        );
                        if (!code)
                          handler.showMessage(
                            "Unable to read QR code.",
                            "error"
                          );
                        else {
                          importFromQR(code.data);
                        }
                      });

                      document.body.removeChild(img);
                    }}
                  />
                  <br />
                  <br />
                  <p>
                    Or, you can scan the QR Code with your camera (you may need
                    to give BQA camera permissions).
                  </p>
                  <div>
                    <canvas
                      id="canvas"
                      hidden
                      style={{ maxHeight: 200 }}
                    ></canvas>
                  </div>
                  <button
                    style={{ marginTop: 40 }}
                    className="back-button"
                    onClick={() => setShowQRImport(false)}
                  >
                    Cancel
                  </button>
                </>
              )}
            </>
          )}
        {phase === "ANSWERING" && <div>{randomized[0].reference}</div>}
        {phase === "RULING" && (
          <div>
            {randomized[0].reference}
            <br />
            <br />
            {getStyledVerse(randomized[0].verse, transcript)}
            {transcript && (
              <>
                <br />
                <br />
                {getStyledVerse(transcript, randomized[0].verse)}
              </>
            )}
          </div>
        )}
        {(phase === "ANSWERING" || phase === "RULING") &&
          displayWords.length > 0 && (
            <div style={{ marginTop: 15 }}>
              {displayWords.map((w, i) => (
                <span
                  key={i}
                  style={{ color: w.status === "C" ? undefined : "red" }}
                >
                  {w.word + " "}
                </span>
              ))}
            </div>
          )}
      </div>
      <div style={{ height: 80 }}>
        {phase === READY &&
          !showQRImport &&
          randomized.length > 0 &&
          (settings.touchscreen ? (
            <p className="important-information">
              Double-tap to see the next verse.
            </p>
          ) : (
            <button className="clickable" onClick={clickNextVerse}>
              Next Verse
            </button>
          ))}
        {phase === ANSWERING &&
          (settings.touchscreen ? (
            <p className="important-information">
              Double-tap to show the verse.
            </p>
          ) : (
            <>
              <button className="clickable" onClick={clickShowAnswer}>
                Show Answer
              </button>
              {stats.current.correct === 0 && (
                <p
                  className="important-information"
                  style={{ marginTop: 20 }}
                >{`Quote the verse, and click Show Answer when you are finished. ${
                  settings.vcVoiceToText
                    ? "The words you speak will show up on the screen, compared to the actual verse. "
                    : ""
                }If you have a keyboard, you can also type the first letter of each word of the verse.`}</p>
              )}
            </>
          ))}
        {phase === RULING &&
          (settings.touchscreen ? (
            <div>
              <p className="important-information">
                Swipe right for 20 Points, left for Error.
              </p>
              <button
                className="link"
                onClick={() => makeRuling(2)}
                style={{ paddingLeft: 0 }}
              >
                Throwout
              </button>
            </div>
          ) : (
            <div>
              <Ruling
                onClick={(type) => makeRuling(type)}
                prioritize={prioritizeRuling}
              />
            </div>
          ))}
      </div>
      <Row>
        {(phase === "READY" || phase === "DONE") && !showQRImport && (
          <div style={{ marginRight: 30 }}>
            <div>{`Total Verses: ${stats.current.totalVerses}`}</div>
            <div>{`Correct: ${stats.current.correct}`}</div>
            <div>{`Incorrect: ${stats.current.incorrect}`}</div>
            <div>
              {`Percentage Correct: ${
                stats.current.correct + stats.current.incorrect === 0
                  ? "0.00"
                  : Math.round(
                      (stats.current.correct * 1000) /
                        (stats.current.correct + stats.current.incorrect)
                    ) / 10.0
              }`}
            </div>
            <div>{`Verses Remaining: ${randomized.length}`}</div>
            <div>{`Total Time: ${getTimeDisplay(stats.current.time)}`}</div>
            <div>{`Time This Verse: ${getTimeDisplay(timeTaken.current)}`}</div>
            {(stats.current.correct > 0 || stats.current.incorrect > 0) && (
              <div>
                {`Average Time: ${getTimeDisplay(
                  stats.current.time /
                    (stats.current.correct + stats.current.incorrect)
                )}`}
              </div>
            )}
          </div>
        )}
        {phase === RULING && (
          <>
            <div
              style={{ marginRight: 30 }}
            >{`Time This Verse: ${getTimeDisplay(timeTaken.current)}`}</div>
            <div style={{ fontSize: 12, minHeight: 110 }}>
              {randomized[0]?.questions?.map((question, index) => (
                <div key={index}>{question}</div>
              ))}
            </div>
          </>
        )}
      </Row>
      {!showQRImport && (
        <Row style={{ marginTop: phase === "ANSWERING" ? 130 : 20 }}>
          <button
            className="back-button"
            onClick={() => {
              if (timekeeperId.current) clearTimeout(timekeeperId.current);
              SpeechRecognition.stopListening();
              navigate("/");
            }}
          >
            Go Back
          </button>
          {phase === "READY" && !showQRExport && randomized.length < 150 && (
            <button
              className="link"
              onClick={() => setShowQRExport(true)}
              style={{ marginLeft: 10 }}
            >
              Export QR Code
            </button>
          )}
        </Row>
      )}
      {showQRExport && (
        <div style={{ marginTop: 20 }}>
          <p>
            You can export your progress to continue later. You can screenshot
            the code or scan it from another device.
          </p>
          <div id="qr-code" style={{ marginTop: 40 }} />
        </div>
      )}
      {handler.snackbar}
    </DoubleClickable>
  );
}
